import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import SectionProcessBg from '../components/SectionProcessBg';
import MarketCard3D, { accentForMarket } from '../components/MarketCard3D';
import { fadeUp, staggerContainer } from '../motion/variants';

const TREND_TICKER = [
  'Flexible packaging ↑ 18% YoY',
  'rPET formulations +24%',
  'NIR-sortable blacks trending',
  'Automotive lightweighting demand',
  'Healthcare compliance surge',
  'UV-stable agricultural film growth',
  'ESD compounds +11% APAC',
];

const MARKETS = [
  {
    no: '01',
    title: 'Flexible Packaging',
    desc: 'Multilayer Pouches · Liquid Packaging · Coating Lamination',
    trend: '+18%',
    trendDir: 'up',
    trendLabel: 'YoY converter demand',
    spark: 'surge',
    image: 'market-01-flexible-packaging.png',
  },
  {
    no: '02',
    title: 'Rigid Packaging',
    desc: 'PET bottles · closures · preforms',
    trend: '+12%',
    trendDir: 'up',
    trendLabel: 'Lightweighting momentum',
    spark: 'up',
    image: 'market-02-rigid-packaging.png',
  },
  {
    no: '03',
    title: 'Automotive',
    desc: 'Interior trim · EV compounds',
    trend: '+9%',
    trendDir: 'up',
    trendLabel: 'EV interior compounds',
    spark: 'high',
    image: 'market-03-automotive.png',
  },
  {
    no: '04',
    title: 'Construction',
    desc: 'Pipes · profiles · geomembranes · sheets',
    trend: '+6%',
    trendDir: 'steady',
    trendLabel: 'Infrastructure polymers',
    spark: 'steady',
    image: 'market-04-construction.png',
  },
  {
    no: '05',
    title: 'Agriculture',
    desc: 'Mulch film · greenhouse cover',
    trend: '+14%',
    trendDir: 'up',
    trendLabel: 'UV-stable film demand',
    spark: 'surge',
    image: 'market-05-agriculture.png',
  },
  {
    no: '06',
    title: 'Healthcare',
    desc: 'Medical-grade · clean-room film',
    trend: '+21%',
    trendDir: 'up',
    trendLabel: 'Regulatory-grade growth',
    spark: 'high',
    image: 'market-06-healthcare.png',
  },
  {
    no: '07',
    title: 'Fiber & Textiles',
    desc: 'Spunbond · polyester yarn · dope-dyed fiber',
    trend: '+5%',
    trendDir: 'steady',
    trendLabel: 'Dope-dyed sustainability',
    spark: 'steady',
    image: 'market-07-fiber-textiles.png',
  },
  {
    no: '08',
    title: 'Electronics',
    desc: 'ESD · flame retardant · housings',
    trend: '+11%',
    trendDir: 'up',
    trendLabel: 'APAC device packaging',
    spark: 'up',
    image: 'market-08-electronics.png',
  },
];

export default function MarketsSection() {
  const reduceMotion = useReducedMotion();
  const ticker = [...TREND_TICKER, ...TREND_TICKER];

  return (
    <section id="markets" className="sec sec-process-wrap markets-v2" data-palette="navy">
      <SectionProcessBg variant="analytics" seed={7} />
      <div className="markets-v2__ambient" aria-hidden="true">
        <span className="markets-v2__chart-bg" />
        <span className="markets-v2__orb markets-v2__orb--1" />
        <span className="markets-v2__orb markets-v2__orb--2" />
        <span className="markets-v2__scan" />
      </div>

      <div className="sec-process-content markets-v2__inner">
        <motion.div
          className="markets-v2__hdr"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div variants={fadeUp}>
            <div className="kicker k-gold">End Markets</div>
            <h2 className="title lt">
              Industries
              <br />
              we <em>serve</em>
            </h2>
          </motion.div>
          <motion.p className="markets-v2__sub" variants={fadeUp}>
            Real-time market signals across polymer converting — where color performance, compliance, and
            recyclability drive growth.
          </motion.p>
        </motion.div>

        <div className="markets-v2__ticker-wrap" aria-hidden="true">
          <div className={`markets-v2__ticker${reduceMotion ? ' markets-v2__ticker--static' : ''}`}>
            {ticker.map((label, i) => (
              <span key={`${label}-${i}`} className="markets-v2__ticker-item">
                <span className="markets-v2__ticker-dot" />
                {label}
              </span>
            ))}
          </div>
        </div>

        <motion.div
          className="markets-v2__grid"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.06 }}
          style={{ perspective: reduceMotion ? undefined : 1600 }}
        >
          {MARKETS.map((m, i) => (
            <MarketCard3D key={m.no} market={m} index={i} accent={accentForMarket(i)} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
